import React, { useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import { ClickAwayListener, Grow, Popper } from '@material-ui/core'
import ScrollContent from '../../molecules/ScrollContent'
import { KEYS } from '../../../constants'
import InlineSearchInput from './InlineSearchInput'
import SelectMenuOption from './SelectMenuOption'

function SinglePopoverMenu ({
  anchorEl,
  open,
  setOpen,
  options,
  pinnedOptions,
  onSearchChange,
  onSelect,
  showIcon,
  hideSearch
}) {
  const handleClose = useCallback(() => setOpen(false), [setOpen])

  const handleEnter = useCallback((e) => {
    if (open && e.which === KEYS.ENTER) {
      const combined = [...(pinnedOptions || []), ...(options || [])]
      if (combined.length === 1) {
        onSelect(combined.at(0))
        e.stopPropagation()
        e.preventDefault()
      }
    }
  }, [open, options, pinnedOptions, onSelect])

  const handleEscape = useCallback((e) => {
    if (open && e.which === KEYS.ESC) {
      setOpen(false)
      e.stopPropagation()
      e.preventDefault()
    }
  }, [open, setOpen])

  // hotkey handlers
  useEffect(() => {
    const handler = (e) => {
      handleEscape(e)
      handleEnter(e)
    }
    window.addEventListener('keydown', handler, false)
    return () => {
      window.removeEventListener('keydown', handler, false)
    }
  }, [handleEscape, handleEnter])

  const animateMenu = useCallback(({ TransitionProps, placement }) => {
    return (
      <Grow {...TransitionProps} style={{ transformOrigin: 'top' }}>
        <ScrollContent className='opal-drop-menu below select-none' maxHeight='300px'>
          <ClickAwayListener onClickAway={handleClose}>
            <div className='flex flex-col' style={{ minWidth: 200 }}>
              {hideSearch ? (
                <div>
                  <InlineSearchInput onChange={onSearchChange} />
                </div>
              ) : null}
              {(pinnedOptions || []).length ? (
                <>
                  {pinnedOptions.map(option => (
                    <SelectMenuOption
                      key={`pi_${option.value}`}
                      option={option}
                      onClick={onSelect}
                      hideAvatar
                      icon={showIcon ? option.icon : null}
                    />
                  ))}
                  <div className='divider' />
                </>
              ) : null}
              {(options || []).map(option => (
                <SelectMenuOption
                  key={`mi_${option.value}`}
                  option={option}
                  onClick={onSelect}
                  hideAvatar
                  icon={showIcon ? option.icon : null}
                />
              ))}
              {((pinnedOptions || []).length + (options || []).length) === 0 ? (
                <div className='p-sm flex justify-center font-size-sm'>
                  No results
                </div>
              ) : null}
            </div>
          </ClickAwayListener>
        </ScrollContent>
      </Grow>
    )
  }, [handleClose, onSelect, onSearchChange, options, pinnedOptions, showIcon, hideSearch])

  return (
    <Popper
      transition
      open={open}
      placement='bottom-start'
      className='z-index-dropdown'
      style={{ zIndex: 10000 }}
      anchorEl={anchorEl}
    >
      {animateMenu}
    </Popper>
  )
}

SinglePopoverMenu.propTypes = {
  anchorEl: PropTypes.any,
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  options: PropTypes.array,
  pinnedOptions: PropTypes.array,
  onSearchChange: PropTypes.func,
  onSelect: PropTypes.func,
  showIcon: PropTypes.bool,
  hideSearch: PropTypes.bool
}

export default SinglePopoverMenu
